import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT Bearer token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Refresh-aware 401 interceptor
const SKIP_REFRESH_URLS = ['/api/v1/auth/refresh', '/api/v1/auth/me'];
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      SKIP_REFRESH_URLS.some((url) => originalRequest.url?.includes(url))
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      isRefreshing = false;
      processQueue(error, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/auth/refresh`,
        null,
        { params: { refresh_token: refreshToken } },
      );
      const { access_token, refresh_token: newRefresh } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefresh);

      processQueue(null, access_token);
      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// ─── Auth ───

export const authAPI = {
  register: (data: { email: string; password: string; display_name?: string }) =>
    api.post('/api/v1/auth/register', data),
  login: (data: { email: string; password: string }) =>
    api.post('/api/v1/auth/login', data),
  forgotPassword: (email: string) =>
    api.post('/api/v1/auth/forgot-password', null, { params: { email } }),
  resetPassword: (token: string, new_password: string) =>
    api.post('/api/v1/auth/reset-password', null, { params: { token, new_password } }),
  verifyEmail: (token: string) =>
    api.get('/api/v1/auth/verify-email', { params: { token } }),
  sendVerification: (email: string) =>
    api.post('/api/v1/auth/send-verification', null, { params: { email } }),
};

// ─── Organizations ───

export const orgsAPI = {
  list: () => api.get('/api/v1/orgs/'),
  create: (data: { name: string; slug?: string }) =>
    api.post('/api/v1/orgs/', data),
  get: (orgId: number) => api.get(`/api/v1/orgs/${orgId}`),
  update: (orgId: number, data: { name?: string }) =>
    api.put(`/api/v1/orgs/${orgId}`, data),
  members: (orgId: number) => api.get(`/api/v1/orgs/${orgId}/members`),
  invite: (orgId: number, data: { email: string; role?: string }) =>
    api.post(`/api/v1/orgs/${orgId}/invite`, data),
  changeRole: (orgId: number, userId: number, role: string) =>
    api.put(`/api/v1/orgs/${orgId}/members/${userId}`, null, { params: { role } }),
  removeMember: (orgId: number, userId: number) =>
    api.delete(`/api/v1/orgs/${orgId}/members/${userId}`),
  switchOrg: (orgId: number) => api.post(`/api/v1/orgs/switch/${orgId}`),
  apiKeys: (orgId: number) => api.get(`/api/v1/orgs/${orgId}/api-keys`),
  createApiKey: (orgId: number, data: { name: string }) =>
    api.post(`/api/v1/orgs/${orgId}/api-keys`, data),
  deleteApiKey: (orgId: number, keyId: number) =>
    api.delete(`/api/v1/orgs/${orgId}/api-keys/${keyId}`),
};

// ─── Health ───

export const healthAPI = {
  check: () => api.get('/health'),
};

// ─── Admin Platform ───

export const adminPlatformAPI = {
  // RBAC
  getRoles: () => api.get('/api/v1/rbac/roles'),
  createRole: (data: { role_name: string; description?: string; permissions?: Record<string, boolean> }) =>
    api.post('/api/v1/rbac/roles', data),
  updateRole: (id: number, data: Record<string, any>) =>
    api.put(`/api/v1/rbac/roles/${id}`, data),
  deleteRole: (id: number) => api.delete(`/api/v1/rbac/roles/${id}`),
  getPermissions: () => api.get('/api/v1/rbac/permissions'),

  // Audit log
  getAuditLog: (params?: { action?: string; actor?: string; from_date?: string; to_date?: string; limit?: number; offset?: number }) =>
    api.get('/api/v1/audit-log/', { params }),
  exportAuditLog: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/api/v1/audit-log/export', { params, responseType: 'blob' }),

  // Admin dashboard
  getStats: (params?: { site_id?: number }) =>
    api.get('/api/v1/admin-dashboard/stats', { params }),
  getSites: () => api.get('/api/v1/admin-dashboard/sites'),
  updateSiteConfig: (siteId: number, data: Record<string, any>) =>
    api.put(`/api/v1/admin-dashboard/sites/${siteId}/config`, data),

  // Feedback admin
  updateFeedbackStatus: (id: number, status: string, admin_notes?: string, resolution_summary?: string) =>
    api.patch(`/api/v1/feedback-admin/${id}/status`, { status, admin_notes, resolution_summary }),
  triageFeedback: (id: number) => api.post(`/api/v1/feedback-admin/${id}/triage`),
  getFeedbackStats: (params?: { site_id?: number }) =>
    api.get('/api/v1/feedback-admin/stats', { params }),

  // Impersonation
  startImpersonation: (data: { site_id: number; target_user_identifier: string; target_user_display?: string; reason?: string }) =>
    api.post('/api/v1/impersonation/start', data),
  endImpersonation: () => api.post('/api/v1/impersonation/end'),
  getActiveImpersonation: () => api.get('/api/v1/impersonation/active'),
  getImpersonationHistory: (params?: { site_id?: number; limit?: number }) =>
    api.get('/api/v1/impersonation/history', { params }),

  // Chat admin
  getChatConfig: (params?: { site_id?: number }) =>
    api.get('/api/v1/chat-admin/config', { params }),
  updateChatConfig: (data: Record<string, any>) =>
    api.put('/api/v1/chat-admin/config', data),
  getChatSessions: (params?: { site_id?: number; limit?: number }) =>
    api.get('/api/v1/chat-admin/sessions', { params }),
  getChatSession: (id: number) => api.get(`/api/v1/chat-admin/sessions/${id}`),

  // Analytics
  getAnalyticsOverview: (params?: { site_id?: number; period?: string }) =>
    api.get('/api/v1/analytics/overview', { params }),
  getFeedbackTrends: (params?: { site_id?: number; period?: string }) =>
    api.get('/api/v1/analytics/feedback-trends', { params }),
  getChatTrends: (params?: { site_id?: number; period?: string }) =>
    api.get('/api/v1/analytics/chat-trends', { params }),
  getGuardrailTrends: (params?: { site_id?: number; period?: string }) =>
    api.get('/api/v1/analytics/guardrail-trends', { params }),

  // Design sessions
  startDesignSession: (data: { feedback_id: number; site_id: number }) =>
    api.post('/api/v1/design-sessions/start', data),
  sendDesignMessage: (sessionId: number, message: string) =>
    api.post(`/api/v1/design-sessions/${sessionId}/message`, { message }),
  acceptDesignSession: (sessionId: number) =>
    api.post(`/api/v1/design-sessions/${sessionId}/accept`),
  abandonDesignSession: (sessionId: number) =>
    api.post(`/api/v1/design-sessions/${sessionId}/abandon`),
  getDesignSession: (sessionId: number) =>
    api.get(`/api/v1/design-sessions/${sessionId}`),

  // Campaigns
  getCampaigns: (params?: { limit?: number; offset?: number }) =>
    api.get('/api/v1/campaigns/', { params }),
  createCampaign: (data: Record<string, any>) =>
    api.post('/api/v1/campaigns/', data),
  getCampaign: (id: number) => api.get(`/api/v1/campaigns/${id}`),
  updateCampaign: (id: number, data: Record<string, any>) =>
    api.put(`/api/v1/campaigns/${id}`, data),
  sendCampaign: (id: number) => api.post(`/api/v1/campaigns/${id}/send`),

  // Triage Config (proxied to UITL backend)
  getTriageConfig: (ventureId: number) =>
    api.get(`/api/v1/ventures/${ventureId}/triage-config`),
  updateTriageConfig: (ventureId: number, data: Record<string, any>) =>
    api.put(`/api/v1/ventures/${ventureId}/triage-config`, data),
  listTriageConfigs: () =>
    api.get('/api/v1/triage-configs'),
};

// ─── Customer Sites ───

export const customerSitesAPI = {
  create: (data: { name: string; domain: string; vertical?: string }) =>
    api.post('/api/v1/customer-sites/', data),
  list: () => api.get('/api/v1/customer-sites/'),
  get: (siteId: number) => api.get(`/api/v1/customer-sites/${siteId}`),
  getSnippet: (siteId: number) => api.get(`/api/v1/customer-sites/${siteId}/snippet`),
  getFeedback: (siteId: number, params?: { status?: string; feedback_type?: string; limit?: number; offset?: number }) =>
    api.get(`/api/v1/customer-sites/${siteId}/feedback`, { params }),
  getFeedbackStats: (siteId: number) => api.get(`/api/v1/customer-sites/${siteId}/feedback/stats`),
  updateSettings: (siteId: number, data: Record<string, any>) =>
    api.patch(`/api/v1/customer-sites/${siteId}/settings`, data),
  regenerateKey: (siteId: number) =>
    api.post(`/api/v1/customer-sites/${siteId}/regenerate-key`),
  deleteSite: (siteId: number) =>
    api.delete(`/api/v1/customer-sites/${siteId}`),
  // Phase 2: Managed hosting
  setupManaged: (siteId: number, data?: { clone_url?: string; max_depth?: number }) =>
    api.post(`/api/v1/customer-sites/${siteId}/setup-managed`, data || {}),
  setupCustomDomain: (siteId: number, domain: string) =>
    api.post(`/api/v1/customer-sites/${siteId}/custom-domain`, { domain }),
  getDomainStatus: (siteId: number) =>
    api.get(`/api/v1/customer-sites/${siteId}/domain-status`),
  redeploy: (siteId: number) =>
    api.post(`/api/v1/customer-sites/${siteId}/redeploy`),
};

// Managed Web Presence (WaaS) — operator surface. Proxies to the Patchwise engine.
export const managedWebPresenceAPI = {
  list: () => api.get('/api/v1/managed-web-presence'),
  create: (data: {
    client_id: string; display_name: string; source_repo: string;
    primary_domain: string; plan_tier: string; billing_email?: string;
    ownership_model?: string; registrant_name?: string; origin?: string;
  }) => api.post('/api/v1/managed-web-presence', data),
  get: (siteId: number) => api.get(`/api/v1/managed-web-presence/${siteId}`),
  promote: (siteId: number) => api.post(`/api/v1/managed-web-presence/${siteId}/promote`),
  reissueCheckout: (siteId: number) => api.post(`/api/v1/managed-web-presence/${siteId}/reissue-checkout`),
  suspend: (siteId: number) => api.post(`/api/v1/managed-web-presence/${siteId}/suspend`),
  resume: (siteId: number) => api.post(`/api/v1/managed-web-presence/${siteId}/resume`),
};

// ─── UITL Backend (Work Board) ───
// The work board API lives on the UITL backend (port 8005).
// nginx proxies /api/v1/work-board and /api/v1/ventures to port 8005.
// Shared JWT secret means the same Bearer token works across both backends.

export const workBoardAPI = {
  check: (data: { venture_id: number; scopes: string[]; worker_id?: string }) =>
    api.post('/api/v1/work-board/check', data),
  create: (data: Record<string, any>) =>
    api.post('/api/v1/work-board/', data),
  list: (params?: Record<string, string>) =>
    api.get('/api/v1/work-board/', { params }),
  active: (params?: Record<string, string>) =>
    api.get('/api/v1/work-board/active', { params }),
  get: (id: number) => api.get(`/api/v1/work-board/${id}`),
  update: (id: number, data: Record<string, any>) =>
    api.put(`/api/v1/work-board/${id}`, data),
  heartbeat: (id: number) =>
    api.post(`/api/v1/work-board/${id}/heartbeat`),
  complete: (id: number) =>
    api.post(`/api/v1/work-board/${id}/complete`),
  delete: (id: number) =>
    api.delete(`/api/v1/work-board/${id}`),
  cleanup: () => api.post('/api/v1/work-board/cleanup'),
  // Ventures list (for filter dropdown)
  ventures: () => api.get('/api/v1/ventures'),
};

// Process Orchestrator API (UITL backend, proxied via nginx like work board)
export const orchestratorAPI = {
  schedule: (params?: Record<string, any>) =>
    api.get('/api/v1/orchestrator/schedule', { params }),
  live: () => api.get('/api/v1/orchestrator/live'),
  history: (params?: Record<string, any>) =>
    api.get('/api/v1/orchestrator/history', { params }),
  stats: (params?: { hours?: number }) =>
    api.get('/api/v1/orchestrator/stats', { params }),
  tonto: () => api.get('/api/v1/orchestrator/tonto'),
  trigger: (configId: number) =>
    api.post(`/api/v1/orchestrator/trigger/${configId}`),
  updateSchedule: (configId: number, data: { cron_expression?: string; enabled?: boolean }) =>
    api.put(`/api/v1/orchestrator/schedule/${configId}`, data),
  ventures: () => api.get('/api/v1/orchestrator/ventures'),
};

export default api;
